import { FC, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { FullScreenModal } from 'src/Components/FullScreenModal'
import { ANY_LANGUAGE_ID } from 'src/Constants/constants'
import { useAppSelector } from 'src/Hooks/redux'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { getCampaignToSchedule } from 'src/Redux/campaign-process/spotifyCampaignSchedule-process/selectors'
import { ProperGenreOrLanguage, SelectedLanguage } from 'src/Types'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { CollapsedSearchInput } from 'src/Components/CollapsedSearchInput'

import { LanguagesModalSpotifyButton } from './LanguagesModalSpotifyButton'
import { LanguagesModalSpotifyWrapperList } from './LanguagesModalSpotifyList'

const Title = styled.div`
  color: ${({ theme }) => theme.common.primaryText};
  font-family: 'Segoe UI';
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  padding-top: 23px;
`

const ListWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  overflow-x: scroll;
  height: calc(100vh - 214px);
  -ms-overflow-style: none;
  scrollbar-width: none;
  height: 100%;
  padding-bottom: 50px;

  &::-webkit-scrollbar {
    display: none;
  }

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    padding: 0 24px 50px;
  }
`

const StyledModal = styled(FullScreenModal)`
  height: 100%;
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  gap: 24px;
  padding-top: 22px;
  margin: 0 auto;
  background: ${({ theme }) => theme.common.primaryBg};
  position: sticky;
  top: 0;
  padding-bottom: 25px;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    z-index: 2000;
  }
`

const StyledSearchInput = styled(CollapsedSearchInput)`
  max-width: 600px;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.common.primaryBorder};
  &.ant-input-affix-wrapper .ant-input-prefix {
    margin-inline-end: 8px;
  }
  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    border-radius: 40px;
    width: 100%;
    input::placeholder {
      visibility: visible;
    }
  }
  &:focus-visible,
  &:focus,
  &:focus-within {
    width: 100%;
    @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
      position: relative;
      width: 100%;
      input::placeholder {
        visibility: visible;
      }
    }
  }
  input::placeholder {
    color: ${({ theme }) => theme.common.primaryPlaceholder};
  }
`

const convertLanguages = (
  languages: ProperGenreOrLanguage[],
): SelectedLanguage | null => {
  const selected = languages.find((language) => language.id !== ANY_LANGUAGE_ID)

  if (!selected) {
    return null
  }
  return {
    value: Number(selected.id),
    label: selected.name,
  }
}

export const LanguagesModalSpotifyWrapper: FC = () => {
  const { t } = useTranslation()
  const campaignDetails = useAppSelector(getCampaignToSchedule)
  const [value, setValue] = useState<string>('')
  const [targetLanguage, setTargetLanguage] = useState<number>()
  const [selectedLanguage, setSelectedLanguage] =
    useState<SelectedLanguage | null>(null)
  const { setSearch } = useCustomSearchParams()

  const handleClose = (): void => {
    setSearch({ modal: null })
  }
  useEffect(() => {
    if (campaignDetails?.languages.length) {
      setSelectedLanguage(convertLanguages(campaignDetails?.languages))
      const anyLanguage = campaignDetails?.languages.find(
        (item) => item.id === ANY_LANGUAGE_ID,
      )?.id as number
      setTargetLanguage(anyLanguage || 0)
    } else {
      setTargetLanguage(ANY_LANGUAGE_ID)
    }
  }, [campaignDetails?.languages, campaignDetails?.languages.length])

  const handleClick = (item: SelectedLanguage): void => {
    if (selectedLanguage?.value === item.value) {
      setSelectedLanguage(null)
      return
    }
    setSelectedLanguage(item)
  }

  return (
    <StyledModal
      showCloseButton
      onCancel={handleClose}
      prefixCls='languages'
      open
    >
      <ListWrapper>
        <Title>{t('scheduleCampaignPage.vocalLanguage')}</Title>

        <InputContainer>
          <StyledSearchInput
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setValue(e.target.value)
            }
            placeholder={t('scheduleCampaignPage.enterLanguageName')}
          />
        </InputContainer>

        <LanguagesModalSpotifyWrapperList
          searchValue={value}
          setValue={setValue}
          selectedLanguage={selectedLanguage}
          targetLanguage={targetLanguage}
          onClick={handleClick}
          onChange={(id: number) => setTargetLanguage(id)}
        />
      </ListWrapper>
      <LanguagesModalSpotifyButton
        targetLanguage={targetLanguage}
        selectedLanguage={selectedLanguage}
        onBackClick={handleClose}
      />
    </StyledModal>
  )
}
